import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Plus, Trash2, Eye, EyeOff, Copy, ExternalLink, Link2, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ProjectTabs from "../components/ProjectTabs";
import SecretsEnv from "../components/SecretsEnv";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ProjectAccessPage() {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState({});
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API_URL}/api/projects/${projectId}/access`, auth)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Impossible de charger les accès"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/projects/${projectId}/access`, data, auth);
      toast.success("Accès enregistrés");
    } catch (e) { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setEnv = (i, k, v) => setData((d) => {
    const envs = [...(d.environments || [])]; envs[i] = { ...envs[i], [k]: v }; return { ...d, environments: envs };
  });
  const addEnv = () => setData((d) => ({ ...d, environments: [...(d.environments || []), { name: "", url: "", note: "" }] }));
  const removeEnv = (i) => setData((d) => ({ ...d, environments: d.environments.filter((_, k) => k !== i) }));
  const setUser = (i, k, v) => setData((d) => {
    const users = [...d.access_users]; users[i] = { ...users[i], [k]: v }; return { ...d, access_users: users };
  });
  const addUser = () => setData((d) => ({ ...d, access_users: [...(d.access_users || []), { label: "", username: "", password: "", note: "" }] }));
  const removeUser = (i) => setData((d) => ({ ...d, access_users: d.access_users.filter((_, k) => k !== i) }));
  const copy = (v) => { navigator.clipboard.writeText(v || ""); toast.success("Copié"); };

  const Tabs = () => (
    <ProjectTabs projectId={projectId} actif="acces" />
  );

  if (loading) {
    return <CRMLayout><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div></CRMLayout>;
  }

  if (user?.role === "intern" || data?.read_only) {
    const liens = [];
    if (data?.access_url) liens.push({ name: "Accès principal", url: data.access_url, note: "Application du projet" });
    for (const env of data?.environments || []) {
      if (env.url && !liens.some((item) => item.url === env.url)) liens.push(env);
    }
    return (
      <CRMLayout>
        <div className="p-6 lg:p-8 max-w-3xl">
          <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4">
            <ArrowLeft className="w-4 h-4" /> Retour aux projets
          </Link>
          <Tabs />
          <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
            Ces liens servent à ouvrir et tester les interfaces du projet. Les identifiants, secrets et accès serveur restent volontairement masqués.
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <h2 className="font-bold text-slate-900 mb-4">Liens d’accès au projet</h2>
            {liens.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">Aucun lien n’est encore renseigné.</div>
            ) : (
              <div className="space-y-3">
                {liens.map((item, index) => (
                  <a key={`${item.url}-${index}`} href={item.url} target="_blank" rel="noreferrer"
                    className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-4 hover:border-indigo-300 hover:bg-indigo-50/40">
                    <div>
                      <p className="font-semibold text-slate-900">{item.name || "Environnement"}</p>
                      <p className="break-all text-sm text-indigo-600">{item.url}</p>
                      {item.note && <p className="mt-1 text-xs text-slate-500">{item.note}</p>}
                    </div>
                    <ExternalLink className="h-5 w-5 shrink-0 text-indigo-600" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </CRMLayout>
    );
  }

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8 max-w-3xl">
        <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour aux projets
        </Link>
        <Tabs />

        {/* Environnements */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-slate-900">Environnements</h2>
            <Button size="sm" variant="outline" onClick={addEnv}><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          {(!data.environments || data.environments.length === 0) ? (
            <div className="text-sm text-slate-400 py-3 text-center">Aucun environnement. Ex : PROD, TEST…</div>
          ) : (
            <div className="space-y-2">
              {data.environments.map((e, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <div className="sm:col-span-2">
                    <Input value={e.name} onChange={(ev) => setEnv(i, "name", ev.target.value)} placeholder="PROD" className="h-9 font-semibold" />
                  </div>
                  <div className="sm:col-span-4 flex gap-1">
                    <Input value={e.url} onChange={(ev) => setEnv(i, "url", ev.target.value)} placeholder="http://…" className="h-9" />
                    {e.url && <a href={e.url} target="_blank" rel="noreferrer" className="border border-slate-200 rounded-lg px-2.5 flex items-center hover:bg-slate-50" title="Ouvrir"><ExternalLink className="w-4 h-4 text-slate-500" /></a>}
                  </div>
                  <div className="sm:col-span-5">
                    <Input value={e.note} onChange={(ev) => setEnv(i, "note", ev.target.value)} placeholder="Note (base, ports…)" className="h-9 text-xs" />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    <button onClick={() => removeEnv(i)} className="text-red-500 hover:bg-red-50 rounded p-2" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lien & port */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-4">
          <h2 className="font-bold text-slate-900 mb-4">Accès à l'application</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1"><Link2 className="w-3.5 h-3.5" /> Lien / URL</label>
              <div className="flex gap-1.5">
                <Input value={data.access_url} onChange={(e) => setField("access_url", e.target.value)} placeholder="https://…" />
                {data.access_url && (
                  <a href={data.access_url} target="_blank" rel="noreferrer" className="border border-slate-200 rounded-lg px-2.5 flex items-center hover:bg-slate-50" title="Ouvrir">
                    <ExternalLink className="w-4 h-4 text-slate-500" />
                  </a>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1"><Server className="w-3.5 h-3.5" /> Port</label>
              <Input value={data.access_port} onChange={(e) => setField("access_port", e.target.value)} placeholder="3000" />
            </div>
          </div>
        </div>

        {/* Utilisateurs */}
        <SecretsEnv projectId={projectId} token={token} />

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-slate-900">Utilisateurs &amp; identifiants</h2>
            <Button size="sm" variant="outline" onClick={addUser}><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>

          {(!data.access_users || data.access_users.length === 0) ? (
            <div className="text-sm text-slate-400 py-6 text-center">Aucun accès enregistré. Cliquez sur « Ajouter ».</div>
          ) : (
            <div className="space-y-3">
              {data.access_users.map((u, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                    <div className="sm:col-span-3">
                      <label className="text-[11px] font-semibold text-slate-500">Rôle / label</label>
                      <Input value={u.label} onChange={(e) => setUser(i, "label", e.target.value)} placeholder="Admin, SSH…" className="h-9" />
                    </div>
                    <div className="sm:col-span-4">
                      <label className="text-[11px] font-semibold text-slate-500">Identifiant</label>
                      <div className="flex gap-1">
                        <Input value={u.username} onChange={(e) => setUser(i, "username", e.target.value)} className="h-9" />
                        <button onClick={() => copy(u.username)} className="border border-slate-200 rounded px-2 hover:bg-slate-50" title="Copier"><Copy className="w-3.5 h-3.5 text-slate-400" /></button>
                      </div>
                    </div>
                    <div className="sm:col-span-4">
                      <label className="text-[11px] font-semibold text-slate-500">Mot de passe</label>
                      <div className="flex gap-1">
                        <Input type={reveal[i] ? "text" : "password"} value={u.password} onChange={(e) => setUser(i, "password", e.target.value)} className="h-9 font-mono" />
                        <button onClick={() => setReveal((r) => ({ ...r, [i]: !r[i] }))} className="border border-slate-200 rounded px-2 hover:bg-slate-50" title="Afficher">
                          {reveal[i] ? <EyeOff className="w-3.5 h-3.5 text-slate-400" /> : <Eye className="w-3.5 h-3.5 text-slate-400" />}
                        </button>
                        <button onClick={() => copy(u.password)} className="border border-slate-200 rounded px-2 hover:bg-slate-50" title="Copier"><Copy className="w-3.5 h-3.5 text-slate-400" /></button>
                      </div>
                    </div>
                    <div className="sm:col-span-1 flex justify-end">
                      <button onClick={() => removeUser(i)} className="text-red-500 hover:bg-red-50 rounded p-2" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <Input value={u.note} onChange={(e) => setUser(i, "note", e.target.value)} placeholder="Note (optionnel : URL admin, remarque…)" className="h-8 mt-2 text-xs" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-4">
          <label className="text-xs font-semibold text-slate-700 block mb-1.5">Notes d'accès (base de données, SSH, clés…)</label>
          <textarea value={data.access_notes} onChange={(e) => setField("access_notes", e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono min-h-[90px]" />
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Enregistrer
          </Button>
          <span className="text-[11px] text-slate-400">Stocké de façon privée sur votre serveur, accessible uniquement après connexion.</span>
        </div>
      </div>
    </CRMLayout>
  );
}
